import { Button } from 'primereact/button'
import { ContextMenu } from 'primereact/contextmenu'
import { Dialog } from 'primereact/dialog'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { PostProcImage, useGetPostProcImages } from '../../api/submission-API'
import PostProcGallery from '../dashboard/PostProcGallery'
import { LoadingIcon } from '../Icons'

type PostProcImagesModalProps = {
  systemRunId: string
  runId: string
  caseName: string
  onClose: () => void
  /** Baseline/ref case gm_id to include as the first case when opening in MAV */
  baselineRunId?: string
  /** Ref2 case gm_id to include as an additional comparison when opening in MAV */
  ref2RunId?: string
}

const THUMBNAIL_LIMIT = 12

const PostProcImagesModal: FC<PostProcImagesModalProps> = ({
  systemRunId,
  runId,
  caseName,
  onClose,
  baselineRunId,
  ref2RunId
}) => {
  const [{ data, loading, error }, fetchImages] = useGetPostProcImages()
  const [selectedImage, setSelectedImage] = useState<PostProcImage | null>(null)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [showFullGallery, setShowFullGallery] = useState(false)
  const contextMenuRef = useRef<ContextMenu>(null)
  const [contextMenuImage, setContextMenuImage] = useState<PostProcImage | null>(null)
  const contextMenuModel = useMemo(
    () => [
      {
        label: 'Open in MAV',
        icon: 'pi pi-external-link',
        command: () => {
          if (contextMenuImage) handleOpenInMav(contextMenuImage)
        }
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contextMenuImage]
  )

  useEffect(() => {
    // Fetch first batch of images when modal opens
    fetchImages(systemRunId, { skip: 0, limit: THUMBNAIL_LIMIT })
  }, [systemRunId])

  const handleImageClick = (image: PostProcImage) => {
    setSelectedImage(image)
    setLightboxOpen(true)
  }

  const handleCloseLightbox = () => {
    setLightboxOpen(false)
    setSelectedImage(null)
  }

  const handleNavigate = (direction: 'prev' | 'next') => {
    if (!data?.images || !selectedImage) return

    const currentIndex = data.images.findIndex(img => img.fullPath === selectedImage.fullPath)
    if (currentIndex === -1) return

    let newIndex: number
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : data.images.length - 1
    } else {
      newIndex = currentIndex < data.images.length - 1 ? currentIndex + 1 : 0
    }
    setSelectedImage(data.images[newIndex])
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const LOCATION_TO_MAV_VIEW: Record<string, string> = {
    Top: 'Top',
    Bottom: 'Bottom',
    Left: 'Left',
    Right: 'Right',
    Front: 'Front',
    Aft: 'Back',
    BottomDiffuser: 'Bottom',
    BottomSplitter: 'Bottom'
  }

  const isDeltaImage = (image: PostProcImage): boolean => {
    const metricType = image.metadata.metricType?.toLowerCase() || ''
    const fullPath = image.fullPath.toLowerCase()

    return (
      fullPath.includes('/compare_pictures/') ||
      metricType.startsWith('d') ||
      metricType.includes('delta') ||
      metricType.includes('dcp') ||
      metricType.includes('dwss')
    )
  }

  const buildMavUrl = (image: PostProcImage): string | null => {
    try {
      const pathParts = image.fullPath.split('/')
      const casesIndex = pathParts.indexOf('cases')
      if (casesIndex < 1) return null
      const raceTeamId = pathParts.slice(0, casesIndex).join('/')
      const runId = pathParts[casesIndex + 1]
      if (!raceTeamId || !runId) return null

      const storageAccount = new URL(image.url).hostname.split('.')[0]
      if (!storageAccount) return null

      const storageContainer = 'cfd-cases'
      const dataPath = `https://${storageAccount}.blob.core.windows.net/${storageContainer}/${raceTeamId}/cases/${runId}/Results/MAV_${runId}`

      const isSection = image.metadata.imageType === 'Sect'

      // Build Cases array — if a baseline ref case exists, it goes first (same convention as the MAV web launcher)
      const cases: Array<Record<string, string>> = []
      if (baselineRunId && String(baselineRunId) !== String(runId)) {
        const baselineDataPath = `https://${storageAccount}.blob.core.windows.net/${storageContainer}/${raceTeamId}/cases/${baselineRunId}/Results/MAV_${baselineRunId}`
        cases.push({
          ID: String(baselineRunId),
          Name: 'Baseline',
          StorageAccount: storageAccount,
          StorageContainer: storageContainer,
          DataPath: baselineDataPath
        })
      }
      const hasBaseline = baselineRunId && String(baselineRunId) !== String(runId)
      cases.push({
        ID: runId,
        Name: hasBaseline ? 'Comparison' : caseName,
        StorageAccount: storageAccount,
        StorageContainer: storageContainer,
        DataPath: dataPath
      })

      // Add ref2 as an additional comparison case if set and different from both baseline and current
      if (
        ref2RunId &&
        String(ref2RunId) !== String(runId) &&
        String(ref2RunId) !== String(baselineRunId || '')
      ) {
        const ref2DataPath = `https://${storageAccount}.blob.core.windows.net/${storageContainer}/${raceTeamId}/cases/${ref2RunId}/Results/MAV_${ref2RunId}`
        cases.push({
          ID: String(ref2RunId),
          Name: 'Comparison 2',
          StorageAccount: storageAccount,
          StorageContainer: storageContainer,
          DataPath: ref2DataPath
        })
      }

      const allIds = cases.map(c => c.ID)
      const sessionName = hasBaseline ? `Session ${allIds.join('-')}` : `Session ${runId}`
      const sessionDesc = hasBaseline
        ? `Comparing ${allIds.slice(1).join(', ')} against baseline ${baselineRunId}`
        : caseName

      const payload: Record<string, unknown> = {
        Name: sessionName,
        Description: sessionDesc,
        LocalStoragePath: '',
        NetworkStoragePath: '',
        Cases: cases
      }

      const sliceAxis =
        isSection && image.metadata.section ? image.metadata.section.split('-')[0] : null
      if (sliceAxis) payload.SliceAxis = sliceAxis
      if (isSection && image.metadata.metricType) payload.SliceVariable = image.metadata.metricType
      if (!isSection && image.metadata.metricType)
        payload.SurfaceVariable = image.metadata.metricType
      if (isSection && image.metadata.value != null)
        payload.SlicePosition = image.metadata.value * 0.0254
      if (isDeltaImage(image)) payload.IsDelta = true
      if (!isSection && image.metadata.location) {
        const mavView = LOCATION_TO_MAV_VIEW[image.metadata.location]
        if (mavView) payload.View = mavView
      }

      return `mav-launcher://${encodeURIComponent(JSON.stringify(payload))}`
    } catch {
      return null
    }
  }

  const handleOpenInMav = (image: PostProcImage) => {
    const url = buildMavUrl(image)
    if (!url) {
      toast.error('Unable to build MAV launch URL for this image.')
      return
    }
    window.open(url, '_blank')
  }

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <LoadingIcon className="w-12 h-12 animate-spin fill-dark-blue dark:fill-light-blue" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading images...</p>
        </div>
      )
    }

    if (error) {
      // Log technical details for debugging, but show generic message to users
      const technicalError = error?.response?.data?.message || error?.message || 'Unknown error'
      console.error('Post-proc images error:', technicalError)

      return (
        <div className="flex flex-col items-center justify-center py-12">
          <i className="pi pi-exclamation-triangle text-4xl text-yellow-500 mb-4" />
          <p className="text-red-500 font-medium">Unable to load images</p>
          <p className="text-sm text-gray-500 mt-2 text-center max-w-md">
            Something went wrong while loading the images. Please try again later.
          </p>
          <Button
            label="Retry"
            className="mt-4 px-4 py-2"
            outlined
            onClick={() => fetchImages(systemRunId, { skip: 0, limit: THUMBNAIL_LIMIT })}
          />
        </div>
      )
    }

    if (!data?.images || data.images.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <p className="text-gray-600 dark:text-gray-400">
            No post-processing images available for this case.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Images will appear here once post-processing completes.
          </p>
        </div>
      )
    }

    return (
      <div className="flex flex-col">
        {/* Image count header */}
        <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
          Showing {data.images.length} of {data.totalCount} images
          {data.hasMore && ' • More images available in gallery'}
        </div>

        {/* Thumbnail grid */}
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {data.images.map(image => (
            <div
              key={image.fullPath}
              className="relative group cursor-pointer rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 aspect-square"
              onClick={() => handleImageClick(image)}
              onContextMenu={e => {
                setContextMenuImage(image)
                contextMenuRef.current?.show(e)
              }}>
              <img
                src={image.url}
                alt={image.name}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-end">
                <div className="w-full p-2 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-white text-xs truncate">{image.name}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View Gallery button */}
        {data.totalCount > THUMBNAIL_LIMIT && (
          <div className="mt-6 flex justify-center">
            <Button
              label={`View All ${data.totalCount} Images`}
              icon="pi pi-images"
              className="p-button-outlined"
              onClick={() => setShowFullGallery(true)}
            />
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      {/* Use Dialog instead of Modal for better control over size when gallery is shown */}
      <Dialog
        visible={true}
        onHide={onClose}
        header={
          showFullGallery ? (
            <div className="flex items-center gap-3">
              <Button
                icon="pi pi-arrow-left"
                rounded
                text
                severity="secondary"
                onClick={() => setShowFullGallery(false)}
                tooltip="Back to preview"
              />
              <span>Post-Processing Images - {caseName}</span>
            </div>
          ) : (
            `Post-Processing Images - ${caseName}`
          )
        }
        style={
          showFullGallery
            ? { width: '95vw', height: '95vh' }
            : { width: '80vw', maxWidth: '1200px' }
        }
        maximizable={showFullGallery}
        modal
        dismissableMask
        className={showFullGallery ? 'p-dialog-maximized' : ''}
        contentStyle={showFullGallery ? { height: 'calc(95vh - 60px)', padding: 0 } : undefined}>
        <div
          className={showFullGallery ? '' : 'mt-4 min-h-[200px]'}
          style={showFullGallery ? { height: '100%' } : undefined}>
          {showFullGallery ? (
            <PostProcGallery
              systemRunId={systemRunId}
              runId={runId}
              caseName={caseName}
              baselineRunId={baselineRunId}
              ref2RunId={ref2RunId}
              onClose={() => setShowFullGallery(false)}
              isExpanded={showFullGallery}
            />
          ) : (
            renderContent()
          )}
        </div>
      </Dialog>

      <ContextMenu ref={contextMenuRef} model={contextMenuModel} />

      {/* Lightbox for full image view */}
      <Dialog
        visible={lightboxOpen}
        onHide={handleCloseLightbox}
        className="max-w-[90vw] max-h-[90vh]"
        header={selectedImage?.name}
        style={{ width: 'auto', maxWidth: '90vw' }}
        modal
        dismissableMask>
        {selectedImage && (
          <div className="flex flex-col items-center">
            <div className="relative">
              <img
                src={selectedImage.url}
                alt={selectedImage.name}
                className="max-w-full max-h-[70vh] object-contain"
              />

              {/* Navigation arrows */}
              {data?.images && data.images.length > 1 && (
                <>
                  <button
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2"
                    onClick={e => {
                      e.stopPropagation()
                      handleNavigate('prev')
                    }}>
                    <i className="pi pi-chevron-left" />
                  </button>
                  <button
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2"
                    onClick={e => {
                      e.stopPropagation()
                      handleNavigate('next')
                    }}>
                    <i className="pi pi-chevron-right" />
                  </button>
                </>
              )}
            </div>

            {/* Image info */}
            <div className="mt-4 text-sm text-gray-600 dark:text-gray-400 flex gap-4">
              <span>{formatFileSize(selectedImage.size)}</span>
              <span>{new Date(selectedImage.lastModified).toLocaleString()}</span>
            </div>

            {/* Action buttons */}
            <div className="mt-4 flex gap-2">
              <Button
                label="Open in MAV"
                icon="pi pi-external-link"
                severity="info"
                size="small"
                onClick={() => handleOpenInMav(selectedImage)}
                tooltip="Open in MAV-Aero"
                tooltipOptions={{ position: 'top' }}
                style={{ color: '#fff' }}
                pt={{
                  icon: { style: { marginRight: '6px', color: '#fff' } },
                  label: {
                    style: {
                      color: '#fff',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '14px',
                      fontWeight: 600,
                      lineHeight: '16px'
                    }
                  }
                }}
              />
              <Button
                label="Download"
                icon="pi pi-download"
                size="small"
                onClick={() => {
                  const link = document.createElement('a')
                  link.href = selectedImage.url
                  link.download = selectedImage.name
                  link.click()
                }}
              />
            </div>
          </div>
        )}
      </Dialog>
    </>
  )
}

export default PostProcImagesModal
